<template>
    <div class="grid deals-container rounded-lg">
        <div v-if="!advertisersData || advertisersData.length === 0" class="col-12 text-center py-6 card">
            <h3 class="text-xl text-white font-light">No advertisers found for filtered markets.</h3>
        </div>
        <div v-else v-for="advertiser in advertisersData" :key="advertiser.id"
            class="col-12 card gap-1 py-2 pl-7 text-white deal-row" @click="$emit('edit-advertiser', advertiser.id)"
            style="background-color: var(--table-background-color)">
            <div class="flex">
                <!-- First name takes full height -->
                <div class="flex flex-col items-start">
                        <span class="font-bold">{{ advertiser.name }}</span>
                        <Button
                            icon="pi pi-sign-in"
                            text
                            severity="secondary"
                            @click="handleLogin(advertiser)"
                            class="mt-2 p-1 h-6 w-6 text-xs"
                            aria-label="Login"
                        />
                </div>
                <div class="flex">
                    <span v-if="!advertiser.logoUrl"
                    class="text-md text-red-500 bg-red-100 ml-2 px-1 py-0 rounded leading-tight"
                    style="line-height: 1; height: 1.2em;">
                        no logo
                    </span>
                    <span v-if="advertiser.voonixAdvertisers.length == 0"
                        class="text-md text-red-500 bg-red-100 ml-2 px-1 py-0 rounded leading-tight"
                        style="line-height: 1; height: 1.2em;">
                        No logins
                    </span>
                    <span v-if="checkInstantLink(advertiser)"
                        class="text-md text-green-500 bg-green-100 ml-2 px-1 py-0 rounded leading-tight text-center"
                        style="line-height: 1; height: 2em; display: inline-block;">
                        Instant<br>link
                    </span>
                </div>
                
                

                <!-- Other names wrap in the remaining space -->
                <div class="flex-grow-1">
                    <div class="flex flex-wrap gap-4">
                        <div v-for="marketDeal in advertiser.marketDeals" :key="marketDeal.marketName"
                            class="font-bold py-3 p-2 rounded-xl" style="background-color: var(--table-row-hover-bg)">
                            {{ marketDeal.marketName }}
                            <span :class="marketClass(marketDeal.marketName)" class="ml-2"></span>
                            <div v-if="marketDeal.distinctDeals && marketDeal.distinctDeals.length"
                                class="flex flex-wrap gap-2 mt-1">
                                <div v-for="(distinctDeal, index) in marketDeal.distinctDeals" :key="index"
                                    class="text-sm deal-container rounded-lg px-1 flex gap-1" :class="{ 'deal-public': distinctDeal.isPublic }">
                                        <span>{{ distinctDeal.dealType.toUpperCase() }} -</span>
                                        <span class="text-sm bg-primary-800 border-round">
                                            {{ getFriendlyDealMessage(distinctDeal.ourDeal) }}
                                        </span>
                                        <span v-if="integrationName(advertiser, distinctDeal)"
                                            class="text-xs text-gray-400 self-center"
                                            :title="'Integration: ' + integrationName(advertiser, distinctDeal)">
                                            @{{ integrationName(advertiser, distinctDeal) }}
                                        </span>
                                        <span v-if="distinctDeal.maxVersion > 1"
                                            class="text-xs text-amber-500 self-center font-semibold"
                                            :title="'This deal has been changed — currently on version ' + distinctDeal.maxVersion">
                                            · edited v{{ distinctDeal.maxVersion }}
                                        </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { getFriendlyDealMessage } from '@/utils/deals';
import { marketClass } from '@/utils/markets';
import { inject } from 'vue';
import { openUrl } from '@/utils/url';


const instantLinks = inject('instantLinks', []);

const props = defineProps({
    dealsData: {
        type: Array,
        required: true
    }
});

defineEmits(['edit-advertiser']);

const advertisersData = computed(() => props.dealsData || []);

const handleLogin = (advertiser) => {
    const activeVoonix = getActiveFirstActivVoonixAdvertiser(advertiser);
    if (activeVoonix) {
        console.log('Login clicked for:', advertiser.name, 'Active Voonix:', activeVoonix);
        // Proceed with your login logic using activeVoonix.
        openUrl(activeVoonix.loginUrl);
    } else {
        console.log('No active voonixAdvertiser found for:', advertiser.name);        
    }
};

const getActiveFirstActivVoonixAdvertiser = (advertiser) => {
    if (!advertiser.voonixAdvertisers || !Array.isArray(advertiser.voonixAdvertisers)) {
        console.log('No voonixAdvertisers found for:', advertiser.name);
        return ;
    }
    return advertiser.voonixAdvertisers.find(v => v.isActive);
}

const checkInstantLink = (advertiser) => {
     const voonixAdvertiser = getActiveFirstActivVoonixAdvertiser(advertiser);
     if (!voonixAdvertiser) {
        console.log('No active voonixAdvertiser found for:', advertiser.name);
        return false;
     }
     return instantLinks.includes(voonixAdvertiser.affiliateSystem);
}

// Name of the Voonix integration a given deal reports under, resolved from the
// advertiser's voonixAdvertisers list. Empty string when it can't be resolved
// (missing/legacy voonixAdvertiserId) so the label is simply hidden.
const integrationName = (advertiser, distinctDeal) => {
    if (!distinctDeal || !distinctDeal.voonixAdvertiserId) return '';
    if (!advertiser || !Array.isArray(advertiser.voonixAdvertisers)) return '';
    const va = advertiser.voonixAdvertisers.find(
        v => String(v.voonixAdvertiserId) === String(distinctDeal.voonixAdvertiserId)
    );
    return va ? va.voonixAdvertiserName : '';
}

</script>

<style scoped>
.deal-row {
    transition: background-color 0.2s;
    border-bottom: 1px solid var(--table-row-hover-bg);
}

.deal-row:hover {
    background-color: var(--table-row-hover-bg) !important;
    cursor: pointer;
}

.deals-container {
    max-height: 70svh;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: var(--primary-color) var(--surface-ground);
}

.deals-container::-webkit-scrollbar {
    width: 8px;
}

.deals-container::-webkit-scrollbar-track {
    background: var(--surface-ground);
}

.deals-container::-webkit-scrollbar-thumb {
    background-color: var(--primary-color);
    border-radius: 4px;
}

.deal-container {
    background-color: var(--table-background-color);
}

.deal-public{
    background-color: #5c3b0a;
}
</style>